import React, { createContext, useContext } from "react";
import { Outlet, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { getPartnerBySlug } from "../api/partner";

// Partner co-branding for /partner/:partnerSlug/:code pages.
// - Every active partner gets its logo next to the tarbaa logo in the headers.
// - Premium partners can also override the brand colors: their theme hex values
//   are mapped onto the CSS variables that tailwind.config.js colors read from,
//   scoped to this subtree only (the rest of the site keeps tarbaa defaults).
const PartnerContext = createContext(null);

// Full fetch state — for pages that must distinguish loading from not-found
// (e.g. the partner landing page redirects home on unknown slugs).
export const usePartnerState = () =>
  useContext(PartnerContext) || { partner: null, isLoading: false, isError: false };

// Just the partner (null outside a partner route or while loading) —
// what the headers use for the co-branded logo.
export const usePartner = () => usePartnerState().partner;

// Theme keys must match tailwind.config.js custom colors and the Partner
// model on the server.
const THEME_KEYS = [
  "header",
  "background",
  "mainButton",
  "mainButtonShadow",
  "secondaryButton",
  "secondaryButtonShadow",
  "behindButton",
  "primary",
  "secondary",
  "mainText",
  "brown",
  "lightBrown",
];

// "#CB8554" -> "203 133 84" (space-separated RGB channels, the format the
// rgb(var(--c-x) / <alpha-value>) tailwind tokens expect).
const hexToChannels = (hex) => {
  if (typeof hex !== "string") return null;
  let h = hex.trim().replace("#", "");
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const n = parseInt(h, 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
};

const PartnerLayout = () => {
  const { partnerSlug } = useParams();

  // Best-effort: the game must render even if the partner lookup fails, so
  // errors are swallowed (no partner -> plain tarbaa branding).
  const {
    data: partner,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["partner", partnerSlug],
    queryFn: () => getPartnerBySlug(partnerSlug),
    enabled: !!partnerSlug,
    retry: false,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const themeVars = {};
  if (partner?.isPremium && partner?.theme) {
    for (const key of THEME_KEYS) {
      const channels = hexToChannels(partner.theme[key]);
      if (channels) themeVars[`--c-${key}`] = channels;
    }
  }

  return (
    <PartnerContext.Provider
      value={{ partner: partner || null, isLoading, isError }}
    >
      {/* display:contents keeps layout untouched while the CSS variables
          still inherit down to every tailwind color token below. */}
      <div className="contents" style={themeVars}>
        <Outlet />
      </div>
    </PartnerContext.Provider>
  );
};

export default PartnerLayout;
